import { accessToken } from './supabase'

/**
 * Обращение к нашему API. Токен подставляется на каждый запрос — сервер по нему
 * узнаёт ученика, куки не используются вовсе.
 */

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
  }
}

async function headers(extra: Record<string, string> = {}) {
  const token = await accessToken()
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  }
}

async function parseError(res: Response): Promise<never> {
  let detail = 'Что-то пошло не так. Попробуй ещё раз.'
  try {
    const body = await res.json()
    if (typeof body?.detail === 'string') detail = body.detail
  } catch {
    /* тело не JSON — оставляем общий текст */
  }
  throw new ApiError(res.status, detail)
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: await headers() })
  if (!res.ok) await parseError(res)
  return res.json()
}

export async function apiSend<T>(
  path: string,
  method: 'POST' | 'PATCH' | 'PUT',
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: await headers(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok) await parseError(res)
  return res.json()
}

/** Событие потока: см. app/web/streaming.py */
export type StreamEvent =
  | { type: 'chunk'; text: string }
  | { type: 'error'; message: string }
  | { type: 'illustration'; url: string; caption?: string | null }
  | ({ type: 'done' } & Record<string, unknown>)

/**
 * Читает построчный JSON по мере поступления.
 *
 * Ответ приходит кусками, поэтому граница строки может попасть в середину
 * куска — остаток держим в буфере до следующего. Без этого длинный ответ
 * разваливался бы на неразбираемые половинки.
 */
export async function* stream(
  path: string,
  init: { body?: unknown; form?: FormData } = {},
): AsyncGenerator<StreamEvent> {
  const isForm = init.form !== undefined
  const res = await fetch(path, {
    method: 'POST',
    headers: await headers(isForm ? {} : { 'Content-Type': 'application/json' }),
    body: isForm ? init.form : init.body === undefined ? undefined : JSON.stringify(init.body),
  })

  if (!res.ok) await parseError(res)
  if (!res.body) {
    yield { type: 'error', message: 'Пустой ответ сервера.' }
    return
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let nl: number
    while ((nl = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, nl).trim()
      buffer = buffer.slice(nl + 1)
      if (line) yield JSON.parse(line) as StreamEvent
    }
  }

  const tail = buffer.trim()
  if (tail) yield JSON.parse(tail) as StreamEvent
}
